import React, { useState, useEffect } from 'react';
import { Card, Empty, Tag, Button, Space, Tooltip, Popconfirm, message, Modal, Form, Input } from 'antd';
import { 
  FileTextOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import { dealsApi, show422Error } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { canManageLeadRecord, canDeleteLeadRecord } from '../../../utils/leadRecordPermissions';

const { TextArea } = Input;

const NotesTab = ({ dealId, data = [], filters, permissions, onRefresh }) => {
  const [notes, setNotes] = useState(data?.notes || []);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    setNotes(data?.notes || []);
  }, [data]);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = moment(dateString);
      return date.format('jYYYY/jMM/jDD HH:mm');
    } catch (e) {
      return null;
    }
  };

  const handleOpenModal = (note = null) => {
    setEditingNote(note);
    if (note) {
      // Load note details for editing
      setEditLoading(true);
      setModalVisible(true);
      dealsApi.getNote(note.id)
        .then((res) => {
          const noteData = res.data?.note || {};
          form.setFieldsValue({
            title: noteData.title || '',
            details: noteData.details || '',
          });
        })
        .catch((error) => {
          console.error('Error fetching note:', error);
          message.error('خطا در دریافت اطلاعات یادداشت');
          setModalVisible(false);
        })
        .finally(() => {
          setEditLoading(false);
        });
    } else {
      // New note
      form.resetFields();
      setModalVisible(true);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingNote(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        deal_id: parseInt(dealId),
        title: values.title,
        details: values.details || '',
      };

      if (editingNote) {
        // Update
        payload.id = editingNote.id;
        await dealsApi.updateNote(payload);
        message.success('یادداشت با موفقیت به‌روزرسانی شد');
      } else {
        // Create
        await dealsApi.createNote(payload);
        message.success('یادداشت با موفقیت ایجاد شد');
      }

      handleCloseModal();
      onRefresh();
    } catch (error) {
      console.error('Error saving note:', error);
      if (error.status === 422) {
        show422Error(error, form);
      } else {
        message.error(editingNote ? 'خطا در به‌روزرسانی یادداشت' : 'خطا در ایجاد یادداشت');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await dealsApi.deleteNote(noteId);
      message.success('یادداشت با موفقیت حذف شد');
      onRefresh();
    } catch (error) {
      console.error('Error deleting note:', error);
      message.error('خطا در حذف یادداشت');
    }
  };

  return (
    <div className="pt-4 px-1 space-y-4">
      <div className="flex items-center justify-end mb-4">
        {canManageLeadRecord(permissions) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal(null)}
            style={{ fontFamily: 'CustomFont' }}
            className="bg-green-600 hover:bg-green-700 border-0"
          >
            افزودن یادداشت
          </Button>
        )}
      </div>

      {/* Notes List */}
      {!notes || notes.length === 0 ? (
        <div className="py-8">
          <Empty
            description="هیچ یادداشتی ثبت نشده است"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <Card
              key={note.id}
              className="shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-700"
              style={{ fontFamily: 'CustomFont' }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <FileTextOutlined className="text-blue-500 text-base flex-shrink-0" />
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 m-0 truncate" style={{ fontFamily: 'CustomFont' }}>
                      {note.title}
                    </h3>
                  </div>

                  {/* Details */}
                  {note.details && (
                    <div 
                      className="text-gray-700 dark:text-gray-300 mb-3 text-sm leading-relaxed"
                      style={{ fontFamily: 'CustomFont' }}
                      dangerouslySetInnerHTML={{ __html: note.details }}
                    />
                  )}

                  {/* Footer Info */}
                  <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                    {note.created_at && (
                      <span style={{ fontFamily: 'CustomFont' }}>
                        ایجاد شده: {formatDate(note.created_at)}
                      </span>
                    )}
                    {note.updated_at && note.updated_at !== note.created_at && (
                      <span style={{ fontFamily: 'CustomFont' }}>
                        به‌روزرسانی: {formatDate(note.updated_at)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <Space className="flex-shrink-0">
                  {canManageLeadRecord(permissions) && (
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      style={{ fontFamily: 'CustomFont' }}
                      title="ویرایش"
                      onClick={() => handleOpenModal(note)}
                      loading={editLoading && editingNote?.id === note.id}
                    />
                  )}
                  {canDeleteLeadRecord(permissions) && (
                    <Popconfirm
                      title="حذف یادداشت"
                      description="آیا از حذف این یادداشت اطمینان دارید؟"
                      onConfirm={() => handleDelete(note.id)}
                      okText="بله، حذف کن"
                      cancelText="لغو"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        className="hover:bg-red-50 dark:hover:bg-red-900/20"
                        style={{ fontFamily: 'CustomFont' }}
                        title="حذف"
                      />
                    </Popconfirm>
                  )}
                </Space>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            {editingNote ? 'ویرایش یادداشت' : 'افزودن یادداشت جدید'}
          </span>
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={700}
        style={{ fontFamily: 'CustomFont' }}
        destroyOnClose
      >
        {editLoading && editingNote ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500" style={{ fontFamily: 'CustomFont' }}>
              در حال بارگذاری اطلاعات...
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ fontFamily: 'CustomFont' }}
            className="mt-4"
          >
            <Form.Item
              name="title"
              label={<span style={{ fontFamily: 'CustomFont' }}>عنوان یادداشت</span>}
              rules={[{ required: true, message: 'لطفاً عنوان یادداشت را وارد کنید' }]}
            >
              <Input
                placeholder="عنوان یادداشت"
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>

            <Form.Item
              name="details"
              label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}
            >
              <TextArea
                rows={6}
                placeholder="توضیحات یادداشت را وارد کنید..."
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  style={{ fontFamily: 'CustomFont' }}
                  className="bg-green-600 hover:bg-green-700 border-0"
                >
                  {editingNote ? 'به‌روزرسانی' : 'ذخیره'}
                </Button>
                <Button
                  onClick={handleCloseModal}
                  disabled={loading}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  لغو
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default NotesTab;
